import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useCallback, useRef } from "react";
const ArtifactUrlCacheContext = createContext(null);
/**
 * Provider component that maintains a cache of resolved artifact URLs.
 * This prevents re-fetching the same artifact URL multiple times during re-renders.
 */
export function ArtifactUrlCacheProvider({ children }) {
    const cacheRef = useRef(new Map());
    const pendingRef = useRef(new Map());
    const getUrl = useCallback((key) => {
        return cacheRef.current.get(key);
    }, []);
    const setUrl = useCallback((key, url) => {
        cacheRef.current.set(key, url);
    }, []);
    const getOrFetch = useCallback(async (key, fetcher) => {
        // Return cached URL if available
        const cached = cacheRef.current.get(key);
        if (cached) {
            return cached;
        }
        // Return pending promise if a fetch is already in progress
        const pending = pendingRef.current.get(key);
        if (pending) {
            return pending;
        }
        // Start new fetch and cache the promise
        const promise = fetcher().then((url) => {
            cacheRef.current.set(key, url);
            pendingRef.current.delete(key);
            return url;
        }).catch((err) => {
            pendingRef.current.delete(key);
            throw err;
        });
        pendingRef.current.set(key, promise);
        return promise;
    }, []);
    const value = React.useMemo(() => ({ getUrl, setUrl, getOrFetch }), [getUrl, setUrl, getOrFetch]);
    return (_jsx(ArtifactUrlCacheContext.Provider, { value: value, children: children }));
}
/**
 * Hook to access the artifact URL cache.
 * Returns null if used outside of ArtifactUrlCacheProvider.
 */
export function useArtifactUrlCache() {
    return useContext(ArtifactUrlCacheContext);
}
/**
 * Generate a cache key for an artifact URL.
 */
export function getArtifactCacheKey(runId, path, disposition = "inline") {
    return `artifact:${runId}:${path}:${disposition}`;
}
/**
 * Generate a cache key for a file URL.
 */
export function getFileCacheKey(path) {
    return `file:${path}`;
}
//# sourceMappingURL=useArtifactUrlCache.js.map